/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SNMPDevice, SNMPInterface, getSNMPInterfaceHistory } from "@/api/snmp";
import { SNMP_REFRESH_INTERVALS } from "@/constants/snmpRefreshIntervals";
import { useSNMPDevice } from "@/hooks/useSNMPDevice";
import { toTrafficPoints } from "@/utils/snmpTraffic";
import { SNMPTrafficChart } from "../SNMPTrafficChart";
import { StatusDot, Tag, fmtBits } from "@/components/servers/kit";
import { SkeletonRows } from "@/components/common/States";
import { cn } from "@/lib/utils";

const OPER_STATUS_UP = 1;
const ADMIN_STATUS_UP = 1;

const statusLabel = (status: number) => {
  switch (status) {
    case 1:
      return "up";
    case 2:
      return "down";
    case 3:
      return "testing";
    default:
      return "unknown";
  }
};

interface SNMPPortsTabProps {
  device: SNMPDevice;
}

type PortFilter = "all" | "up" | "down";

const HISTORY_WINDOWS = [60, 120, 240] as const;

export const SNMPPortsTab: React.FC<SNMPPortsTabProps> = ({ device }) => {
  const [filter, setFilter] = useState<PortFilter>("all");
  const [historyLimit, setHistoryLimit] = useState<number>(120);
  const { interfaces = [], isLoadingInterfaces } = useSNMPDevice({ device });

  const filtered = interfaces.filter((iface) => {
    if (filter === "up") return iface.latest?.operStatus === OPER_STATUS_UP;
    if (filter === "down") return iface.latest?.operStatus !== OPER_STATUS_UP;
    return true;
  });

  const upCount = interfaces.filter((i) => i.latest?.operStatus === OPER_STATUS_UP).length;

  if (isLoadingInterfaces) return <SkeletonRows rows={5} />;

  return (
    <div className="space-y-3">
      <div className="sw-bar">
        <span className="gh-sub">{`${upCount} of ${interfaces.length} ports up`}</span>
        <div className="flex flex-wrap items-center gap-3">
          <div className="sw-seg" role="group" aria-label="History window" title="History window (data points per port)">
            {HISTORY_WINDOWS.map((w) => (
              <button key={w} type="button" aria-pressed={historyLimit === w} onClick={() => setHistoryLimit(w)}>
                {w}
              </button>
            ))}
          </div>
          <div className="sw-seg" role="group" aria-label="Filter ports">
            {([["all", "All"], ["up", "Up"], ["down", "Down"]] as [PortFilter, string][]).map(([f, label]) => (
              <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="sw-empty">No ports match this filter.</div>
      ) : (
        <div className="sw-ports">
          {filtered.map((iface) => (
            <SNMPPortCard key={iface.id} device={device} iface={iface} limit={historyLimit} />
          ))}
        </div>
      )}
    </div>
  );
};

const SNMPPortCard: React.FC<{ device: SNMPDevice; iface: SNMPInterface; limit: number }> = ({
  device,
  iface,
  limit,
}) => {
  const isUp = iface.latest?.operStatus === OPER_STATUS_UP;
  const name = iface.ifAlias || iface.ifDescr || `Port ${iface.ifIndex}`;

  const { data: history } = useQuery({
    queryKey: ["snmp-interface-history", device.id, iface.id, limit],
    queryFn: () => getSNMPInterfaceHistory(device.id, iface.id, 1, limit),
    refetchInterval: SNMP_REFRESH_INTERVALS.HISTORY,
    enabled: isUp,
  });

  const points = history ? toTrafficPoints(history.data) : [];
  const hasErrors =
    !!iface.latest &&
    (iface.latest.inErrors > 0 ||
      iface.latest.outErrors > 0 ||
      iface.latest.inDiscards > 0 ||
      iface.latest.outDiscards > 0);

  const speed = iface.ifSpeed > 0 ? fmtBits(iface.ifSpeed / 8) : "";

  if (!isUp) {
    return (
      <div className="sw-port sw-port--down">
        <div className="sw-port-top">
          <StatusDot state="idle" />
          <b>{name}</b>
          <Tag>{statusLabel(iface.latest?.operStatus ?? 0)}</Tag>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("sw-port", hasErrors && "sw-port--err")}>
      <div className="sw-port-top">
        <StatusDot state="up" />
        <b title={name}>{name}</b>
        {speed && <Tag>{speed}</Tag>}
        {hasErrors && <Tag tone="warn">errors</Tag>}
      </div>
      <small>{`Admin ${statusLabel(iface.latest?.adminStatus ?? ADMIN_STATUS_UP)} · ifIndex ${iface.ifIndex}`}</small>
      <SNMPTrafficChart data={points} />
    </div>
  );
};
